import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    TextField, 
    SelectChangeEvent, 
    useMediaQuery,
    Dialog,
    InputAdornment,
    DialogContent,
    DialogActions,
    Menu,        
    MenuItem,
    Popover,
    Select
} from '@mui/material';
import { useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import DatePicker from '../../Components/DatePicker/date-picker.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import QueryWidget from '../../Components/QueryWidget/query-widget.tsx';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import '../../Components/Button/button-style.scss';
import { getTripsOrdersReport, getVehicleUsageReport, getClients, getWarehouses } from './action.ts'; 

const REPORT_TYPE_OPTIONS = [
    { id: 'Vehicle Usage', label: 'Vehicle Usage' },
    { id: 'Trips', label: 'Trips' },
    { id: 'Orders', label: 'Orders' }
];

const DATE_FILTER_OPTIONS = [
    { id: 'Today', label: 'Today' },
    { id: 'Yesterday', label: 'Yesterday' },
    { id: 'This month', label: 'This month' },
    { id: 'Last month', label: 'Last month' },
    { id: 'Custom', label: 'Custom' }
];

const Reports: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedReport, setSelectedReport] = useState<string>('Vehicle Usage');
    const [dateFilter, setDateFilter] = useState<string>('Today');
    const [vehicleSearch, setVehicleSearch] = useState<string>('');
    const [tempVehicleSearch, setTempVehicleSearch] = useState<string>('');
    const [clientOptions, setClientOptions] = useState<{id: string, label: string}[]>([{ id: 'All', label: 'All Clients' }]);
    const [selectedClient, setSelectedClient] = useState<{id: string, label: string}>({ id: 'All', label: 'All Clients' });
    const [selectedWarehouse, setSelectedWarehouse] = useState<{id: string, label: string}>({ id: 'All', label: 'All Warehouses' });
    const [warehouseOptions, setWarehouseOptions] = useState<{id: string, label: string}[]>([{ id: 'All', label: 'All Warehouses' }]);
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const isFilterOpen = Boolean(filterAnchorEl);
    const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
    const [customFromDate, setCustomFromDate] = useState<string>('');
    const [customToDate, setCustomToDate] = useState<string>('');
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
    const isDownloadMenuOpen = Boolean(downloadMenuAnchor);
    const [allWarehouses, setAllWarehouses] = useState<any[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<string>('0');
    const [pageSize, setPageSize] = useState<string>('10');
    const [totalRecords, setTotalRecords] = useState<number>(0);

    useEffect(() => {
        getClients(
            (response: any) => {
                const data = response?.message?.clients_vendors || [];
                const formattedClients = data.map((item: any) => ({
                    id: item.id.toString(),
                    label: item.name
                }));
                setClientOptions([{ id: 'All', label: 'All Clients' }, ...formattedClients]);
            },
            (error: any) => {
                console.error("Failed to fetch clients", error);
            }
        );

        getWarehouses(
            (response: any) => {
                const data = response?.message?.warehouses || [];
                setAllWarehouses(data); 
            },
            (error: any) => {
                console.error("Failed to fetch warehouses", error);
            }
        );
    }, []);

    useEffect(() => {
        const isSpecificClient = selectedClient && selectedClient.id !== 'All';
        
        const filteredWarehouses = isSpecificClient
            ? allWarehouses.filter((warehouse: any) =>
                warehouse.client_name?.toLowerCase() === selectedClient.label.toLowerCase()
            )
            : allWarehouses;

        const options = filteredWarehouses.map((warehouse: any) => ({
            id: warehouse.id.toString(),
            label: warehouse.name,
        }));

        if (isSpecificClient && options.length === 0) {
            setWarehouseOptions([{ id: 'none', label: 'No Warehouses' }]);
            setSelectedWarehouse({ id: 'none', label: 'No Warehouses' });
        } else {
            setWarehouseOptions([{ id: 'All', label: 'All Warehouses' }, ...options]);
        }
    }, [selectedClient, allWarehouses]);

    useEffect(() => {
        const baseColumns: GridColDef[] = [
            { field: 'sl_no', headerName: 'Sl. No.', width: 80, headerAlign: 'center', align: 'center' },
            { field: 'date', headerName: 'Date', flex: 1, minWidth: 110, headerAlign: 'center', align: 'center' },
            { field: 'vehicle_no', headerName: 'Vehicle No:', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
            { 
                field: 'warehouse_name',
                headerName: 'Warehouse', 
                flex: 1, 
                minWidth: 130, 
                headerAlign: 'center', 
                align: 'center'
            },
            { field: 'start_time', headerName: 'Start Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'end_time', headerName: 'End Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'kms', headerName: 'Kms', width: 90, headerAlign: 'center', align: 'center' },
            { field: 'stops', headerName: 'Stops', width: 90, headerAlign: 'center', align: 'center' },
            { field: 'running_duration', headerName: 'Running Duration', flex: 1, minWidth: 140, headerAlign: 'center', align: 'center' },
        ];

        const usageAndTripsColumns: GridColDef[] = [
            ...baseColumns,
            { field: 'engine_stops', headerName: 'Engine Stops', flex: 1, headerAlign: 'center', align: 'center' }
        ];

        const ordersColumns: GridColDef[] = [
            ...baseColumns,
            { field: 'orders_picked', headerName: 'Orders Picked', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'orders_delivered', headerName: 'Orders Delivered', flex: 1, headerAlign: 'center', align: 'center' }
        ];

        if (selectedReport === 'Orders') {
            setColumns(ordersColumns);
        } else {
            setColumns(usageAndTripsColumns);
        }

        if (!(dateFilter === 'Custom' && isDateModalOpen)) {
            fetchReportData(); 
        }
    }, [selectedReport, page, pageSize, dateFilter, selectedClient, selectedWarehouse, vehicleSearch]);

    const formatDuration = (totalSeconds: number | null) => {
        if (!totalSeconds) return "0 hr, 0 mins";
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        if (hours === 0) return `${minutes} mins`;
        return `${hours} hr, ${minutes} mins`;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    };

    const getDateRange = (filter: string) => {
        if (filter === 'Custom') {
            return { from_date: customFromDate, to_date: customToDate };
        }

        const today = new Date();
        let fromDate = new Date();
        let toDate = new Date();

        if (filter === 'Yesterday') {
            fromDate.setDate(today.getDate() - 1);
            toDate.setDate(today.getDate() - 1);
        } else if (filter === 'This month') {
            fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        } else if (filter === 'Last month') {
            fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        }

        const format = (d: Date) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            from_date: format(fromDate),
            to_date: format(toDate)
        };
    };

    const formatTimeAMPM = (timeString: string) => {
        if (!timeString) return '-';
        
        const safeDateString = timeString.replace(' ', 'T');
        const date = new Date(safeDateString);
        
        if (isNaN(date.getTime())) return timeString;
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const fetchReportData = (apiFormat: string = '') => {
        if (dateFilter === 'Custom' && (!customFromDate || !customToDate)) return;

        setLoading(true);
        const { from_date, to_date } = getDateRange(dateFilter);

        const handleSuccess = (response: any) => {
            const rawData = response?.reports || response?.data || [];
            
            const mappedData = Array.isArray(rawData) ? rawData.map((item: any, index: number) => {            
                const rawStartTime = selectedReport === 'Vehicle Usage' 
                    ? (item.first_moving_start_ist || item.start_time) 
                    : (item.start_time_formatted || item.start_time);
                    
                const rawEndTime = selectedReport === 'Vehicle Usage' 
                    ? (item.last_moving_end_ist || item.end_time) 
                    : (item.end_time_formatted || item.end_time);

                return {
                    ...item,
                    id: item.vehicles_id || index + 1, 
                    sl_no: (parseInt(page) * parseInt(pageSize)) + index + 1,
                    date: item.date,
                    vehicle_no: item.vehicle_number || item.vehicle_no || '',
                    start_time: formatTimeAMPM(rawStartTime),
                    end_time: formatTimeAMPM(rawEndTime),
                    
                    kms: item.total_km || item.kms || '',
                    stops: item.total_stops || item.stops || '',
                    running_duration: item.moving_duration_sec ? formatDuration(item?.moving_duration_sec) : formatDuration(item?.running_duration_sec) ,
                    engine_stops: item?.engine_stop_count || item?.engine_stops || '',
                };
            }) : [];

            setRows(mappedData);
            const totalCount = response?.pagination?.total_records || response?.total_records || mappedData.length;
            setTotalRecords(totalCount);
            setLoading(false);
        };

        const handleError = (errorMsg: any) => {
            toast.error(typeof errorMsg === 'string' ? errorMsg : `Failed to fetch ${selectedReport} report`);
            setRows([]);
            setTotalRecords(0);
            setLoading(false);
        };

        const apiWarehouse = selectedWarehouse.label === 'All Warehouses' ? '' : selectedWarehouse.label;
        const apiClient = selectedClient.label === 'All Clients' ? '' : selectedClient.label;
        
        if (selectedReport === 'Vehicle Usage') {
            getVehicleUsageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleSuccess, handleError, apiWarehouse, apiClient);
        } else {
            getTripsOrdersReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleSuccess, handleError, apiWarehouse, apiClient);
        }
    };

    const handleReportChange = (event: SelectChangeEvent<string>) => {
        setSelectedReport(event.target.value);
        setPage('0'); 
        setDateFilter('Today');
        setCustomFromDate('');
        setCustomToDate('');
        setSelectedClient({ id: 'All', label: 'All Clients' });
        setSelectedWarehouse({ id: 'All', label: 'All Warehouses' });
        setTempVehicleSearch('');
        setVehicleSearch('');
    };

    const handleDateChange = (event: SelectChangeEvent<string>) => {
        const selectedValue = event.target.value;
        setDateFilter(selectedValue);
        
        if (selectedValue === 'Custom') {
            setIsDateModalOpen(true);
        } else {
            setPage('0');
            setVehicleSearch(tempVehicleSearch);
        }
    };

    const handleApplyCustomDates = () => {
        if (!customFromDate || !customToDate) {
            toast.warning("Please select both From and To dates.");
            return;
        }

        const from = new Date(customFromDate);
        const to = new Date(customToDate);

        if (to < from) {
            toast.warning("'To' date cannot be before 'From' date.");
            return;
        }

        const diffTime = Math.abs(to.getTime() - from.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 

        if (diffDays > 30) {
            toast.warning("Date range cannot exceed 30 days.");
            return;
        }

        setIsDateModalOpen(false);
        setVehicleSearch(tempVehicleSearch);
        setPage('0');
        fetchReportData(); 
    };

    const handleCloseModal = () => {
        setIsDateModalOpen(false);
        if (!customFromDate || !customToDate) {
            setDateFilter('Today');
        }
    };

    const getMaxToDate = () => {
        if (!customFromDate) return undefined;
        const fromDateObj = new Date(customFromDate);
        fromDateObj.setDate(fromDateObj.getDate() + 30);
        return fromDateObj.toISOString().split('T')[0];
    };

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => { 
        setPage((newPage - 1).toString()); 
    };

    const handleOpenDownloadMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setDownloadMenuAnchor(null);
    };

    const handleApplyFilter = () => {
        setVehicleSearch(tempVehicleSearch);
        setPage('0'); 
    };

  const handleDownloadFormat = (format: 'Excel' | 'CSV' | 'PDF') => {
        handleCloseDownloadMenu();

        if (totalRecords === 0) {
            toast.warning("No data available to download.");
            return;
        }

        let apiFormat = '';
        let mimeType = '';
        let fileExtension = '';

        if (format === 'Excel') {
            apiFormat = 'xlsx';
            mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            fileExtension = 'xlsx';
        } else if (format === 'CSV') {
            apiFormat = 'csv';
            mimeType = 'text/csv';
            fileExtension = 'csv';
        } else if (format === 'PDF') {
            apiFormat = 'pdf';
            mimeType = 'application/pdf';
            fileExtension = 'pdf';
        }

        const { from_date, to_date } = getDateRange(dateFilter);
        const apiWarehouse = selectedWarehouse.label === 'All Warehouses' ? '' : selectedWarehouse.label;
        const apiClient = selectedClient.label === 'All Clients' ? '' : selectedClient.label;

        const toastId = toast.loading(`Downloading ${format} file... Please wait.`);

        const handleDownloadSuccess = (response: any) => {
            try {
                const binaryData = response.data || response; 

                const blob = new Blob([binaryData], { type: mimeType });
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                
                const fileName = `${selectedReport.replace(/\s+/g, '_')}_Report.${fileExtension}`;
                link.setAttribute('download', fileName);
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.update(toastId, { render: `${format} downloaded successfully!`, type: "success", isLoading: false, autoClose: 3000 });
            } catch (error) {
                console.error("Blob conversion error:", error);
                toast.update(toastId, { render: "Error processing the downloaded file.", type: "error", isLoading: false, autoClose: 3000 });
            }
        };

        const handleDownloadError = (errorMsg: any) => {
            toast.update(toastId, { render: typeof errorMsg === 'string' ? errorMsg : `Failed to download ${format}`, type: "error", isLoading: false, autoClose: 3000 });
        };

        if (selectedReport === 'Vehicle Usage') {
            getVehicleUsageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError, apiWarehouse, apiClient);
        } else {
            getTripsOrdersReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError, apiWarehouse, apiClient);
        }
    };

    const reportAndDateFilters = (
        <Box display="flex" alignItems="center" gap={2}>
            <Dropdown
                options={REPORT_TYPE_OPTIONS}
                selectedValue={selectedReport}
                handleChange={handleReportChange}
                width="180px"
                height="40px" 
            />
            <Box display="flex" alignItems="center" gap={0.5}>
                <Select
                    value={dateFilter}
                    onChange={handleDateChange}
                    sx={{ width: '150px', height: '40px' }}
                >
                    {DATE_FILTER_OPTIONS.map((option) => (
                        <MenuItem 
                            key={option.id} 
                            value={option.id}
                            onClick={() => {
                                if (option.id === 'Custom') {
                                    setIsDateModalOpen(true);
                                }
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                
                {dateFilter === 'Custom' && (
                    <IconButton 
                        onClick={() => setIsDateModalOpen(true)}
                        size="small"
                        sx={{ color: '#000000', height: '40px' }}
                        title="Edit Custom Dates"
                    >
                        <EditIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            <Header />
            <NavBar selected="Reports" /> 
            <QueryWidget />
            <div style={{ marginTop: "70px" }}>
                <Heading 
                    title="Reports" 
                    closeIcon="false" 
                    leftContent={
                        <Box display="flex" alignItems="center" gap={2}>
                            {!isFilterOpen && reportAndDateFilters}
                        </Box>
                    } 
                    rightContent={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Button 
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)} 
                                sx={{ 
                                    visibility: isFilterOpen ? 'hidden' : 'visible',
                                    backgroundColor: isFilterOpen ? 'rgba(0,0,0,0.05)' : 'rgba(0, 0, 0, 0)', 
                                    color: 'rgba(0, 0, 0, 0.54)', 
                                    minWidth: '40px', 
                                    height: '40px' 
                                }}
                                aria-label="Filter"
                            >
                                <FilterAltIcon />
                            </Button>

                            <Button 
                                className="custom-button" 
                                onClick={handleOpenDownloadMenu} 
                                aria-controls={isDownloadMenuOpen ? 'download-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isDownloadMenuOpen ? 'true' : undefined}
                                sx={{ height: '40px' }}
                            >
                                <DownloadIcon />
                            </Button>
                            
                            <Menu
                                id="download-menu"
                                anchorEl={downloadMenuAnchor}
                                open={isDownloadMenuOpen}
                                onClose={handleCloseDownloadMenu}
                            >
                                <MenuItem onClick={() => handleDownloadFormat('Excel')}>Excel</MenuItem>
                                <MenuItem onClick={() => handleDownloadFormat('CSV')}>CSV</MenuItem>
                                <MenuItem onClick={() => handleDownloadFormat('PDF')}>PDF</MenuItem>
                            </Menu>
                        </Box>
                    } 
                />

                <Box 
                    className="table-container custom-data-grid" 
                >                  
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        hideFooterPagination
                        disableColumnResize
                        disableRowSelectionOnClick
                        autoHeight={false}
                        rowCount={totalRecords}
                        classes={{ columnHeader: 'red-header' }}
                    />
                </Box>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginLeft: '180px', marginBottom: '40px' }}>
                    <Newpagination 
                        rows={totalRecords} 
                        pageSize={pageSize} 
                        page={page} 
                        handlePageChange={handlePageClick} 
                    />
                </div>
            </div>

            <Popover
                open={isFilterOpen}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'center',  
                    horizontal: 'right', 
                }}
                sx={{ ml: -1 }} 
            >
                <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    gap: 2, 
                    flexWrap: 'nowrap',
                    width: 'max-content'
                }}>
                    
                    {isFilterOpen && reportAndDateFilters}

                    <Dropdown
                        options={clientOptions}
                        selectedValue={selectedClient.id}
                        handleChange={(e: SelectChangeEvent<string>) => {
                            const selectedId = e.target.value;
                            const fullObj = clientOptions.find(c => c.id === selectedId) || { id: 'All', label: 'All Clients' };
                            setSelectedClient(fullObj);
                            setVehicleSearch(tempVehicleSearch);
                            setPage('0'); 
                        }}
                        width="150px"
                        height="40px"
                    />

                    <Dropdown
                        options={warehouseOptions}
                        selectedValue={selectedWarehouse.id}
                        handleChange={(e: SelectChangeEvent<string>) => {
                            const selectedId = e.target.value;
                            const fullObj = warehouseOptions.find(w => w.id === selectedId) || { id: 'All', label: 'All Warehouses' };
                            setSelectedWarehouse(fullObj);
                            setVehicleSearch(tempVehicleSearch);
                            setPage('0');
                        }}
                        width="150px"
                        height="40px"
                    />
                    
                    <TextField 
                        variant="outlined" 
                        placeholder="Enter Vehicle No..." 
                        value={tempVehicleSearch}
                        onChange={(e: { target: { value: any; }; }) => {
                            const newValue = e.target.value;
                            setTempVehicleSearch(newValue);
                            if (newValue.trim() === '') {
                                setVehicleSearch('');
                                setPage('0');
                            }
                        }}
                        onKeyDown={(e: { key: string; }) => {
                            if (e.key === 'Enter') handleApplyFilter();
                        }}
                        sx={{ 
                            width: '220px', 
                            '& .MuiInputBase-root': { height: '40px', paddingRight: '8px' } 
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton 
                                        onClick={handleApplyFilter} 
                                        edge="end" 
                                        title="Search"
                                        sx={{ color: '#1976d2' }} 
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center" 
                        sx={{ 
                            color: 'rgba(0, 0, 0, 0.54)', 
                            minWidth: '40px', 
                            height: '40px', 
                            backgroundColor: 'rgba(0,0,0,0.05)', 
                            borderRadius: '4px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
                        }}
                        onClick={() => setFilterAnchorEl(null)}
                    >
                        <FilterAltIcon />
                    </Box>

                </Box>
            </Popover>

            <Dialog open={isDateModalOpen} onClose={handleCloseModal}>
                <Box sx={{ marginLeft: '-180px' }}>
                    <Heading title="Select Custom Date Range" closeIcon='true' onClose={handleCloseModal} />
                </Box>
                <DialogContent sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <DatePicker 
                        id="fromDate" 
                        name="fromDate" 
                        value={customFromDate} 
                        title="From"
                        maxDate={customToDate ? new Date(customToDate) : undefined} 
                        onChange={(e: any) => {
                            const newFromDate = e.target.value;
                            setCustomFromDate(newFromDate);
                            if (customToDate && (newFromDate > customToDate)) {
                                setCustomToDate('');
                            }
                        }} 
                    />
                    <DatePicker 
                        id="toDate" 
                        name="toDate" 
                        value={customToDate} 
                        title="To" 
                        onChange={(e: any) => setCustomToDate(e.target.value)} 
                        minDate={customFromDate ? new Date(customFromDate) : undefined} 
                        maxDate={getMaxToDate() ? new Date(getMaxToDate()!) : undefined} 
                        disabled={!customFromDate} 
                    />
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button className="custom-cancel-button" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button className="custom-button" onClick={handleApplyCustomDates}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Reports;